import { Component, OnInit, Input, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, Observable } from 'rxjs';
import { takeUntil, withLatestFrom, filter } from 'rxjs/operators';
import * as fromComm from 'src/app/modules/communications/store/communications';
import { ChannelUIds, ConfiguredChannel, TeamGroupInfo } from 'src/app/modules/communications/store/communications/communications.model';
import { cloneDeep } from 'lodash-es';
import { GUID_EMPTY } from '@app/core/constants';
import { CommunicationsApiService } from '@app/modules/communications/store/communications/communications-api.service';

@Component({
  selector: 'topo-microsoft-teams-edit',
  templateUrl: './microsoft-teams-edit.component.html',
  styleUrls: ['./microsoft-teams-edit.component.scss'],
  standalone: false
})
export class MicrosoftTeamsEditComponent implements OnInit, OnDestroy {
  @Input() isActive: boolean;
  configuredChannel: ConfiguredChannel;
  configuredChannels: ConfiguredChannel[] = [];
  sendDirectMessages: boolean;

  destroy$ = new Subject<void>();
  emptyGuid = GUID_EMPTY;
  hasDirectMessages: boolean;
  isNewMode: boolean;
  teamGroups: TeamGroupInfo[] = [];
  selectedTeamId: string;

  constructor(
    private store: Store<fromComm.State>,
    private changeDetector: ChangeDetectorRef,
    private communicationsApiService: CommunicationsApiService
  ) {}

  ngOnInit(): void {
    this.store
      .select(fromComm.selectMicrosoftTeamsEditIsActive)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isActive => {
        this.isActive = isActive;
        this.checkIfCanAdd();
      });

    this.store
      .select(fromComm.selectSelectedConfiguredChannel)
      .pipe(
        withLatestFrom(this.store.select(fromComm.selectMicrosoftTeamsEditIsActive)),
        filter(([, isActive]) => isActive),
        takeUntil(this.destroy$)
      )
      .subscribe(([selectedChannel, isActive]) => {
        this.teamGroups = [];
        this.configuredChannel = !!selectedChannel ? cloneDeep(selectedChannel) : null;
        this.isNewMode = this.configuredChannel?.uId === GUID_EMPTY;
        this.sendDirectMessages = this.configuredChannel?.settings?.sendDirectMessages === true;
        this.selectedTeamId = this.configuredChannel?.settings?.teamId;
        if (!!this.configuredChannel) {
          this.communicationsApiService.getChannelTeamGroups(this.configuredChannel).subscribe(res => {
            this.teamGroups = res;
          });
        }
        this.checkIfCanAdd();
      });

    this.store
      .select(fromComm.selectConfiguredChannels)
      .pipe(takeUntil(this.destroy$))
      .subscribe(channels => {
        this.configuredChannels = channels?.filter(c => c.channelUId === ChannelUIds.MicrosoftTeamsChannelUId);
        this.checkIfCanAdd();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.store.dispatch(fromComm.closeConfiguredChannelEdit());
  }

  cancelClick(): void {
    this.close();
  }

  selectTeam(): void {
    const updateChannel = {
      ...this.configuredChannel,
      settings: {
        ...this.configuredChannel?.settings,
        teamId: this.selectedTeamId
      }
    };

    this.configuredChannel = updateChannel;
  }

  syncChannelMembers(): void {
    this.store.dispatch(fromComm.syncChannelMembersWithApi({ channelUId: this.configuredChannel.uId }));
  }

  private checkIfCanAdd(): void {
    if (this.isNewMode) {
      this.hasDirectMessages = false;
      return;
    }
    this.hasDirectMessages = this.configuredChannels?.some(c => c.settings?.sendDirectMessages === true);
    this.changeDetector.markForCheck();
  }

  checkDirectMessage(): void {
    if (this.isNewMode !== true) {
      return;
    }
    this.sendDirectMessages = !this.sendDirectMessages;

    const updateChannel = {
      ...this.configuredChannel,
      settings: {
        ...this.configuredChannel?.settings,
        sendDirectMessages: this.sendDirectMessages
      }
    };

    this.configuredChannel = updateChannel;
  }

  saveClick(): void {
    this.store.dispatch(fromComm.saveConfiguredChannelToApi({ channel: this.configuredChannel }));
  }
}
